import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { isSpecialEventTemplate, sortByCamelHumanNameComparator } from '@/common/utils'
import { DEFAULT_DISCOUNT_DEPTH } from '@/features/config/types'
import { useEventDetailsBaseStore } from '@/features/eventDetails/useEventDetailsBaseStore'
import { useEventDetailsDepthsStore } from '@/features/eventDetails/useEventDetailsDepthsStore'
import { useEventDetailsStoreData } from '@/features/eventDetails/useEventDetailsStoreData'
import { useSkusStore } from '@/features/skus/useSkusStore'
import { type SkuOption } from '@/types'

export function useEventDetailsProducts() {
	const { storeId } = storeToRefs(useEventDetailsBaseStore())
	const { customDepths, recommendedDepths } = storeToRefs(useEventDetailsDepthsStore())
	const { skusBySkuId } = storeToRefs(useSkusStore())
	const { selectedSkuIds } = useEventDetailsStoreData()

	/**
	 * List of currently selected SKUs enriched with recommended and currently set depths for each SKU.
	 */
	const products = computed(() => {
		if (!storeId.value) {
			return []
		}

		return selectedSkuIds.value
			.reduce<SkuOption[]>((acc, skuId) => {
				const sku = skusBySkuId.value[skuId]

				if (sku && !isSpecialEventTemplate(sku)) {
					acc.push({
						storeId: storeId.value ?? '',
						humanName: sku.human_name,
						uniqueSkuId: sku.unique_sku_id,
						discountDepth:
							(customDepths.value.has(sku.unique_sku_id)
								? customDepths.value.get(sku.unique_sku_id)
								: recommendedDepths.value.get(sku.unique_sku_id)) ?? DEFAULT_DISCOUNT_DEPTH
					})
				}

				return acc
			}, [])
			.sort(sortByCamelHumanNameComparator)
	})

	return {
		products,
		selectedSkuIds
	}
}
